/**
 * boundaryStyle — adapter between the backend's `boundary_style` and GeoStyler.
 *
 * The backend stores the org-boundary layer's appearance as a **partial map-layer
 * object** — `{ geometry, initialVisibility, style }`, where `style` is a flat
 * MapLibre *paint* dict merged over the backend's defaults (see the 14 Sep
 * backend→frontend note). Every other styled surface in this app (CM tables,
 * spatial datasets, the Geotagged Photos layer) stores a renderer-neutral
 * GeoStyler `Style` and renders through one shared converter, so this module is
 * the seam that lets the boundary join them:
 *
 *  - READ  (`boundaryStyleToGeostyler`)  flat paint dict → one-rule GeoStyler Style.
 *  - WRITE (`geostylerToBoundaryStyle`)  GeoStyler Style → the PATCH body's shape.
 *
 * Directly modelled on {@link ./spatialDatasetStyle.ts}'s
 * `spatialDatasetStyleToGeostyler`, which exists to perform exactly this upgrade
 * for spatial datasets' legacy flat styles.
 *
 * ## Why not just hand the paint dict to MapLibre?
 *
 * Because `style` merges over the defaults, a `geometry: "fill"` boundary carries
 * BOTH paint families in one object (`line-color` + `line-width` alongside
 * `fill-color` + `fill-opacity`). A MapLibre `fill` layer with `line-*` in its
 * paint is an invalid layer spec, and a single fill layer's only outline is
 * `fill-outline-color` — locked at 1px and not widenable. A filled boundary
 * therefore loses the outline the admin just configured.
 *
 * As a GeoStyler rule carrying a `Fill` *and* a `Line` symbolizer,
 * `geostylerToMapLibreLayers` emits two fragments, each with only its own paint
 * family, and `useMapLayerZOrder` ranks `fill` beneath `line` so the outline stays
 * on top. (A preview map without that hook must order the fragments itself.)
 */
import type { FillSymbolizer, LineSymbolizer, Style, Symbolizer } from "geostyler-style";

/**
 * The backend's own fallback paint, reproduced exactly. An org with no stored
 * `boundary_style` must render byte-identically to how it did before the field
 * existed, so these two values are a contract with
 * `_build_org_boundary_map_layer()`, not a frontend preference.
 */
export const BOUNDARY_DEFAULT_LINE_COLOR = "#c0392b";
export const BOUNDARY_DEFAULT_LINE_WIDTH = 1.5;

/**
 * The derived boundary layer's `source` (and `id`). The backend re-applies both
 * after the stored style, so no config write can repoint them — which makes this
 * a reliable way to recognise the layer in `mapLayers[]`.
 */
export const ORG_BOUNDARY_SOURCE = "org-boundary";

/**
 * True for the boundary layer the backend derives from `Organisation.geometry`,
 * including the extra fragments the converter renders it as (`…::p1`).
 */
export const isOrgBoundaryLayer = (layer: { id?: string; source?: string } | null | undefined): boolean =>
  layer?.source === ORG_BOUNDARY_SOURCE || layer?.id === ORG_BOUNDARY_SOURCE || Boolean(layer?.id?.startsWith(`${ORG_BOUNDARY_SOURCE}::`));

/** GeoStyler `Style.name` for the boundary, matching the layer's `id`/`source`. */
export const BOUNDARY_STYLE_NAME = ORG_BOUNDARY_SOURCE;

/**
 * The stored `boundary_style`, and equally the `org-boundary` entry in
 * `mapLayers[]` — the backend applies the former onto the latter, so the two
 * share a shape and either can be read by {@link boundaryStyleToGeostyler}.
 */
export interface BoundaryStyleValue {
  /** Which MapLibre paint family the layer draws. Absent behaves as `"line"`. */
  geometry?: "line" | "fill";
  /** Whether the layer starts switched on. */
  initialVisibility?: boolean;
  /** Flat MapLibre paint properties. */
  style?: Record<string, any>;
  /**
   * The authoritative style. The backend spreads the stored object onto the layer
   * wholesale, so this key survives verbatim (confirmed and pinned with a backend
   * test, 15 Sep) — which puts the boundary on the same stored format as CM
   * tables and spatial datasets.
   *
   * Read in preference to `style`, ALWAYS — never by checking whether `style` is
   * absent. The backend emits `style` unconditionally, with its defaults merged
   * in, so "no `style`" is a state that never occurs on the wire.
   */
  geostyler?: Style;
}

/** True for a value already shaped like a GeoStyler `Style`. */
export const isGeostylerStyle = (value: unknown): value is Style =>
  !!value && typeof value === "object" && Array.isArray((value as Style).rules);

/** Assigns `target[key]` only when `value` survives {@link clean}. */
const set = (target: Record<string, unknown>, key: string, value: unknown): void => {
  const cleaned = clean(value);
  if (cleaned !== undefined) target[key] = cleaned;
};

/**
 * Normalises one stored paint value, so nothing unusable ever reaches GeoStyler
 * or a PATCH body. `boundary_style` is unvalidated by the backend, and the form
 * is not the only thing that can write it — an empty string from a cleared
 * control, or a `null`, is stored verbatim and silently breaks the paint.
 *
 * Arrays and objects pass through untouched: a `line-width` may legitimately be a
 * zoom expression (`["interpolate", ...]`), which the backend forwards unchanged.
 */
const clean = (value: unknown): unknown => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "string" && value.trim() === "") return undefined;
  if (typeof value === "number" && !Number.isFinite(value)) return undefined;
  return value;
};

/**
 * Upgrades a `boundary_style` (or the `org-boundary` map layer carrying one) into
 * a one-rule GeoStyler style.
 *
 * A `Line` symbolizer is ALWAYS emitted — a boundary without an outline isn't a
 * boundary — defaulting to the backend's red at 1.5px. A `Fill` is emitted only
 * for `geometry: "fill"`, so the `fill-*` keys stay inert while the layer is in
 * line mode, exactly as they are today.
 *
 * `outlineColor` decides what `buildFillLayer` writes as `fill-outline-color` — a
 * 1px hairline sitting directly under the real `Line` fragment. For a SOLID line
 * it is pinned to the line colour, so no seam of a different colour peeks out
 * along the edge.
 *
 * For a DASHED line it must not be: a solid 1px hairline in the line's own colour
 * fills in every gap, and a 1.5px dash over it is indistinguishable from a plain
 * line. (That is exactly what a `{geometry: "fill", "line-dasharray": [2, 2]}`
 * boundary rendered as before this branch.) There is no way to switch
 * `fill-outline-color` off, so it is set to the fill's own colour instead, where
 * it disappears into the fill and leaves the dash to read on its own.
 */
export function boundaryStyleToGeostyler(source?: BoundaryStyleValue | null, name = BOUNDARY_STYLE_NAME): Style {
  // Forward path: once the backend round-trips GeoStyler, prefer it outright.
  if (isGeostylerStyle(source?.geostyler)) return source!.geostyler as Style;
  if (isGeostylerStyle(source)) return source as unknown as Style;

  const paint = source?.style ?? {};
  const lineColor = clean(paint["line-color"]) ?? BOUNDARY_DEFAULT_LINE_COLOR;
  const dasharray = clean(paint["line-dasharray"]);
  const dashed = Array.isArray(dasharray) && dasharray.length > 0;
  const symbolizers: Symbolizer[] = [];

  if (source?.geometry === "fill") {
    const fillColor = clean(paint["fill-color"]) ?? lineColor;
    const fill: Record<string, unknown> = {
      kind: "Fill",
      color: fillColor,
      outlineColor: dashed ? fillColor : lineColor
    };
    set(fill, "opacity", paint["fill-opacity"]);
    symbolizers.push(fill as unknown as FillSymbolizer);
  }

  const line: Record<string, unknown> = {
    kind: "Line",
    color: lineColor,
    width: clean(paint["line-width"]) ?? BOUNDARY_DEFAULT_LINE_WIDTH
  };
  set(line, "opacity", paint["line-opacity"]);
  set(line, "dasharray", dasharray);
  symbolizers.push(line as unknown as LineSymbolizer);

  return { name, rules: [{ name: "boundary", symbolizers }] } as Style;
}

/** The first symbolizer of a given kind, across every rule. */
const findSymbolizer = <T extends Symbolizer>(style: Style | undefined, kind: Symbolizer["kind"]): T | undefined => {
  for (const rule of style?.rules ?? []) {
    const found = (rule.symbolizers ?? []).find((s) => s?.kind === kind);
    if (found) return found as T;
  }
  return undefined;
};

/** The boundary's `Fill` symbolizer, if it has one (i.e. the fill toggle is on). */
export const findBoundaryFill = (style?: Style): FillSymbolizer | undefined => findSymbolizer<FillSymbolizer>(style, "Fill");

/** The boundary's `Line` symbolizer. Always present on a style this module built. */
export const findBoundaryLine = (style?: Style): LineSymbolizer | undefined => findSymbolizer<LineSymbolizer>(style, "Line");

/**
 * Collapses a GeoStyler style back into the `boundary_style` the API accepts.
 *
 * Writes the style BOTH ways: `geostyler` is the authoritative document, `style`
 * the flat MapLibre downgrade. The dual write stays even though the backend now
 * round-trips `geostyler` — dropping it would not leave a flat-dict reader (RN
 * mobile) with nothing, it would leave them with the backend's DEFAULTS, since
 * `style` is emitted merged whether or not anything was stored. Silently
 * reverting mobile to a red 1.5px line is worse than carrying a second copy.
 *
 * Two further rules, both from §3 of the backend note:
 *
 *  - **Keys are omitted, never blanked.** `{"line-width": ""}` is stored verbatim
 *    and breaks the paint — the backend does not validate. A cleared control must
 *    drop its key so the backend default fills in.
 *  - **Both paint families are always written.** `style` is the downgrade path for
 *    any client that doesn't read GeoStyler (RN mobile, an older web build), and a
 *    flat-dict reader picks whichever family `geometry` selects. Writing only the
 *    active family would leave such a client with an unstyled layer the moment an
 *    admin toggles fill on.
 */
export function geostylerToBoundaryStyle(style: Style, initialVisibility?: boolean): BoundaryStyleValue {
  const fill = findBoundaryFill(style);
  const line = findBoundaryLine(style);

  const paint: Record<string, unknown> = {};
  set(paint, "line-color", line?.color ?? BOUNDARY_DEFAULT_LINE_COLOR);
  set(paint, "line-width", line?.width ?? BOUNDARY_DEFAULT_LINE_WIDTH);
  set(paint, "line-opacity", line?.opacity);
  set(paint, "line-dasharray", line?.dasharray);
  set(paint, "fill-color", fill?.color);
  set(paint, "fill-opacity", fill?.opacity);

  const value: BoundaryStyleValue = { geometry: fill ? "fill" : "line", style: paint, geostyler: style };
  if (initialVisibility !== undefined) value.initialVisibility = initialVisibility;
  return value;
}

/**
 * Attaches a `geostyler` key to the derived `org-boundary` map layer so the render
 * path uses the shared converter instead of `transformToMapLibreLayer`'s legacy
 * branches. `style` and `geometry` are left on the layer untouched — the same
 * dual-carry `buildGeotaggedPhotosLayer` uses, so a consumer reading either one
 * sees the same boundary.
 */
export const withBoundaryGeostyler = <T extends Record<string, any>>(layer: T): T =>
  ({ ...layer, geostyler: boundaryStyleToGeostyler(layer as BoundaryStyleValue) }) as T;

/**
 * True when a boundary style is indistinguishable from the backend's default —
 * i.e. the org has never actually styled its boundary.
 *
 * `GET /api/v2/config/` always returns the *merged* layer, so an org with no
 * stored `boundary_style` is indistinguishable by shape from one that stored the
 * defaults verbatim. Without this check the config editor's round-trip would
 * quietly convert "never set" into "pinned to today's defaults", and a later
 * change to the backend's default would stop reaching that org.
 */
export function isDefaultBoundaryStyle(value?: BoundaryStyleValue | null): boolean {
  if (!value) return true;
  if (value.initialVisibility === false) return false;
  // A stored GeoStyler document can express things the flat dict cannot (a zoom
  // range, a second rule), so it can flatten to the default while being nothing
  // of the sort. Its presence alone means the org has styled its boundary.
  if (isGeostylerStyle(value.geostyler)) return false;
  const { geometry, style } = geostylerToBoundaryStyle(boundaryStyleToGeostyler(value));
  return (
    geometry === "line" &&
    Object.keys(style ?? {}).length === 2 &&
    style!["line-color"] === BOUNDARY_DEFAULT_LINE_COLOR &&
    style!["line-width"] === BOUNDARY_DEFAULT_LINE_WIDTH
  );
}
